import React, { useEffect, useMemo, useState } from 'react'
import {
   Alert,
   Box,
   Button,
   Chip,
   CircularProgress,
   Divider,
   InputAdornment,
   Paper,
   TextField,
   Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined'
import MemoryIcon from '@mui/icons-material/Memory'
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined'
import axios from '../../api/axios'

const normalizeBoschNumber = (value) =>
   String(value || '')
      .replace(/[^0-9a-z]/gi, '')
      .toUpperCase()

const normalizeRegistration = (value) =>
   String(value || '')
      .trim()
      .toUpperCase()
      .replace(/[\s-]/g, '')

const extractDtcCodes = (value) => {
   const matches =
      String(value || '')
         .toUpperCase()
         .match(/\b[PBCU][0-9A-F]{4}\b/g) || []

   return [...new Set(matches)]
}

const Field = ({ label, value, mono = false }) => (
   <Box
      sx={{
         py: 1.5,
         display: 'grid',
         gridTemplateColumns: { xs: '1fr', sm: '190px 1fr' },
         gap: { xs: 0.4, sm: 2 },
         borderBottom: '1px solid #EEF1F4',
         '&:last-of-type': { borderBottom: 0 },
      }}
   >
      <Typography
         sx={{
            color: '#667085',
            fontSize: '13px',
            fontWeight: 600,
         }}
      >
         {label}
      </Typography>

      <Typography
         sx={{
            color: '#172B4D',
            fontSize: '14px',
            fontWeight: value ? 600 : 400,
            fontFamily: mono ? 'monospace' : 'inherit',
            overflowWrap: 'anywhere',
         }}
      >
         {value || '—'}
      </Typography>
   </Box>
)

const SectionTitle = ({ children }) => (
   <Typography
      sx={{
         mt: 2.2,
         mb: 0.5,
         color: '#163A5F',
         fontSize: '12px',
         fontWeight: 800,
         textTransform: 'uppercase',
         letterSpacing: '0.06em',
      }}
   >
      {children}
   </Typography>
)

export default function Toolbox() {
   const [database, setDatabase] = useState(null)
   const [loading, setLoading] = useState(true)
   const [loadError, setLoadError] = useState('')
   const [query, setQuery] = useState('')
   const [searchedNumber, setSearchedNumber] = useState('')
   const [result, setResult] = useState(null)

   const [vehicleQuery, setVehicleQuery] = useState('')
   const [vehicleLoading, setVehicleLoading] = useState(false)
   const [vehicleError, setVehicleError] = useState('')
   const [vehicleResult, setVehicleResult] = useState(null)

   const [dtcDatabase, setDtcDatabase] = useState(null)
   const [dtcLoading, setDtcLoading] = useState(true)
   const [dtcLoadError, setDtcLoadError] = useState('')
   const [dtcQuery, setDtcQuery] = useState('')
   const [dtcInputError, setDtcInputError] = useState('')
   const [dtcResults, setDtcResults] = useState([])

   useEffect(() => {
      let active = true

      fetch('/data/bosch_ecu.json', { cache: 'no-store' })
         .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`)
            return response.json()
         })
         .then((data) => {
            if (!active) return
            setDatabase(data)
            setLoading(false)
         })
         .catch((error) => {
            if (!active) return

            setLoadError(
               `Bosch reference database could not be loaded (${error.message}).`
            )

            setLoading(false)
         })

      return () => {
         active = false
      }
   }, [])

   useEffect(() => {
      let active = true

      fetch('/data/dtc_codes.json', { cache: 'no-store' })
         .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`)
            return response.json()
         })
         .then((data) => {
            if (!active) return

            setDtcDatabase(data)
            setDtcLoading(false)
         })
         .catch((error) => {
            if (!active) return

            setDtcLoadError(
               `DTC reference database could not be loaded (${error.message}).`
            )

            setDtcLoading(false)
         })

      return () => {
         active = false
      }
   }, [])

   const databaseCount = useMemo(
      () => Number(database?.count || 0),
      [database]
   )

   const dtcItems = useMemo(
      () => dtcDatabase?.items || dtcDatabase || {},
      [dtcDatabase]
   )

   const dtcDatabaseCount = useMemo(
      () => Object.keys(dtcItems).length,
      [dtcItems]
   )

   const searchVehicle = async () => {
      const reg = normalizeRegistration(vehicleQuery)

      setVehicleError('')
      setVehicleResult(null)

      if (!reg) return

      setVehicleLoading(true)

      try {
         const response = await axios.get(
            `toolbox/vehicle/${encodeURIComponent(reg)}`
         )

         if (!response?.data?.status || !response?.data?.data) {
            throw new Error(
               response?.data?.message || 'Vehicle could not be found.'
            )
         }

         setVehicleQuery(reg)
         setVehicleResult(response.data.data)
      } catch (error) {
         const status = error?.response?.status

         const message =
            error?.response?.data?.message ||
            error?.response?.data?.data ||
            error?.message ||
            'Vehicle lookup failed.'

         if (status === 404) {
            setVehicleError(`No vehicle data found for ${reg}.`)
         } else if (status === 401) {
            setVehicleError('Your session is not authorized. Sign in again.')
         } else {
            setVehicleError(message)
         }
      } finally {
         setVehicleLoading(false)
      }
   }

   const searchDtc = () => {
      if (!dtcDatabase) return

      const codes = extractDtcCodes(dtcQuery)

      setDtcInputError('')

      if (!codes.length) {
         setDtcResults([])

         setDtcInputError(
            'Enter at least one valid DTC code, for example P0601 or U0100.'
         )

         return
      }

      setDtcResults(
         codes.map((code) => ({
            code,
            entry: dtcItems[code] || null,
         }))
      )
   }

   const handleDtcKeyDown = (event) => {
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
         event.preventDefault()
         searchDtc()
      }
   }

   const search = () => {
      if (!database?.items) return

      const number = normalizeBoschNumber(query)

      setSearchedNumber(number)

      if (!number) {
         setResult(null)
         return
      }

      setResult(database.items[number] || null)
   }

   const handleKeyDown = (event) => {
      if (event.key === 'Enter') search()
   }

   const handleVehicleKeyDown = (event) => {
      if (event.key === 'Enter' && !vehicleLoading) {
         searchVehicle()
      }
   }

   const renderVehicleResult = () => {
      if (!vehicleResult) return null

      const power =
         vehicleResult.power_kw !== null &&
         vehicleResult.power_kw !== undefined
            ? `${vehicleResult.power_kw} kW${
                 vehicleResult.power_hp
                    ? ` / ${Math.round(vehicleResult.power_hp)} hp`
                    : ''
              }`
            : vehicleResult.power

      const odometer =
         vehicleResult.odometer_km !== null &&
         vehicleResult.odometer_km !== undefined
            ? `${Number(vehicleResult.odometer_km).toLocaleString('sv-SE')} km`
            : vehicleResult.odometer

      return (
         <Paper
            elevation={0}
            sx={{
               mt: 2.5,
               border: '1px solid #E4E7EC',
               borderRadius: 2,
               overflow: 'hidden',
            }}
         >
            <Box
               sx={{
                  px: { xs: 2, sm: 3 },
                  py: 2,
                  bgcolor: '#F8FAFC',
                  borderBottom: '1px solid #E4E7EC',
                  display: 'flex',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1,
               }}
            >
               <Box>
                  <Typography
                     sx={{
                        color: '#172B4D',
                        fontWeight: 800,
                        fontSize: '18px',
                     }}
                  >
                     {vehicleResult.model ||
                        vehicleResult.make ||
                        'Vehicle'}
                  </Typography>

                  <Typography
                     sx={{
                        color: '#667085',
                        fontSize: '13px',
                        mt: 0.3,
                     }}
                  >
                     {vehicleResult.registration_number ||
                        vehicleResult.query}
                  </Typography>
               </Box>

               <Box
                  sx={{
                     display: 'flex',
                     gap: 1,
                     flexWrap: 'wrap',
                  }}
               >
                  {vehicleResult.vehicle_year && (
                     <Chip
                        label={vehicleResult.vehicle_year}
                        size="small"
                        sx={{
                           bgcolor: '#F2F4F7',
                           color: '#475467',
                           fontWeight: 700,
                        }}
                     />
                  )}

                  <Chip
                     label="Transportstyrelsen"
                     size="small"
                     sx={{
                        bgcolor: '#ECFDF3',
                        color: '#067647',
                        fontWeight: 700,
                     }}
                  />
               </Box>
            </Box>

            <Box
               sx={{
                  px: { xs: 2, sm: 3 },
                  pb: 2,
               }}
            >
               <SectionTitle>Vehicle</SectionTitle>

               <Field
                  label="Registration"
                  value={vehicleResult.registration_number}
                  mono
               />

               <Field
                  label="VIN / chassis"
                  value={vehicleResult.vin}
                  mono
               />

               <Field
                  label="Make"
                  value={vehicleResult.make}
               />

               <Field
                  label="Model"
                  value={vehicleResult.model}
               />

               <Field
                  label="Vehicle year"
                  value={vehicleResult.vehicle_year}
               />

               <Field
                  label="Manufactured"
                  value={vehicleResult.manufactured}
               />

               <Field
                  label="Type"
                  value={vehicleResult.type}
                  mono
               />

               <Field
                  label="Variant"
                  value={vehicleResult.variant}
                  mono
               />

               <Field
                  label="Version"
                  value={vehicleResult.version}
                  mono
               />

               <Field
                  label="Color"
                  value={vehicleResult.color}
               />

               <Field
                  label="Status"
                  value={vehicleResult.status}
               />

               <Field
                  label="Imported"
                  value={vehicleResult.imported}
               />

               <SectionTitle>
                  Engine / drivetrain
               </SectionTitle>

               <Field
                  label="Fuel"
                  value={vehicleResult.fuel}
               />

               <Field
                  label="Displacement"
                  value={vehicleResult.displacement}
               />

               <Field
                  label="Power"
                  value={power}
               />

               <Field
                  label="Gearbox"
                  value={vehicleResult.gearbox}
               />

               <Field
                  label="Emission class"
                  value={vehicleResult.emission_class}
               />

               <Field
                  label="Maximum speed"
                  value={vehicleResult.max_speed}
               />

               <SectionTitle>
                  Inspection
               </SectionTitle>

               <Field
                  label="First registered in Sweden"
                  value={
                     vehicleResult.first_registered_sweden
                  }
               />

               <Field
                  label="Last inspection"
                  value={vehicleResult.last_inspection}
               />

               <Field
                  label="Inspection due"
                  value={vehicleResult.inspection_due}
               />

               <Field
                  label="Odometer"
                  value={odometer}
               />

               <SectionTitle>
                  Weights / dimensions
               </SectionTitle>

               <Field
                  label="Curb weight"
                  value={vehicleResult.curb_weight}
               />

               <Field
                  label="Gross weight"
                  value={vehicleResult.gross_weight}
               />

               <Field
                  label="Length"
                  value={vehicleResult.length}
               />

               <Field
                  label="Width"
                  value={vehicleResult.width}
               />

               <Field
                  label="Height"
                  value={vehicleResult.height}
               />
            </Box>
         </Paper>
      )
   }

   const renderResult = (entry, index = 0) => {
      const enriched = Boolean(
         entry?.m ||
            entry?.o ||
            entry?.v ||
            entry?.c ||
            entry?.e
      )

      return (
         <Paper
            key={`${searchedNumber}-${index}`}
            elevation={0}
            sx={{
               mt: 2.5,
               border: '1px solid #E4E7EC',
               borderRadius: 2,
               overflow: 'hidden',
            }}
         >
            <Box
               sx={{
                  px: { xs: 2, sm: 3 },
                  py: 2,
                  bgcolor: '#F8FAFC',
                  borderBottom: '1px solid #E4E7EC',
                  display: 'flex',
                  alignItems: {
                     xs: 'flex-start',
                     sm: 'center',
                  },
                  justifyContent: 'space-between',
                  flexDirection: {
                     xs: 'column',
                     sm: 'row',
                  },
                  gap: 1,
               }}
            >
               <Box>
                  <Typography
                     sx={{
                        color: '#172B4D',
                        fontWeight: 800,
                        fontSize: '18px',
                     }}
                  >
                     {entry.t || 'Bosch ECU'}
                  </Typography>

                  <Typography
                     sx={{
                        color: '#667085',
                        fontSize: '13px',
                        mt: 0.3,
                     }}
                  >
                     Bosch {searchedNumber}
                  </Typography>
               </Box>

               <Box
                  sx={{
                     display: 'flex',
                     gap: 1,
                     flexWrap: 'wrap',
                  }}
               >
                  {entry.f && (
                     <Chip
                        label={entry.f}
                        size="small"
                        sx={{
                           bgcolor: '#E9F6FA',
                           color: '#0D6F88',
                           fontWeight: 700,
                        }}
                     />
                  )}

                  <Chip
                     label={
                        enriched
                           ? 'Detailed reference'
                           : 'Index reference'
                     }
                     size="small"
                     sx={{
                        bgcolor: enriched
                           ? '#ECFDF3'
                           : '#F2F4F7',
                        color: enriched
                           ? '#067647'
                           : '#475467',
                        fontWeight: 700,
                     }}
                  />
               </Box>
            </Box>

            <Box
               sx={{
                  px: { xs: 2, sm: 3 },
                  py: 1,
               }}
            >
               <Field
                  label="Bosch number"
                  value={searchedNumber}
                  mono
               />

               <Field
                  label="ECU type"
                  value={entry.t}
               />

               <Field
                  label="ECU family"
                  value={entry.f}
               />

               <Field
                  label="Manufacturer"
                  value={entry.m}
               />

               <Field
                  label="OEM number(s)"
                  value={entry.o}
                  mono
               />

               <Field
                  label="Vehicle model"
                  value={entry.v}
               />

               <Field
                  label="Product category"
                  value={entry.c}
               />

               <Field
                  label="Brand"
                  value={entry.b || 'Bosch'}
               />
            </Box>

            {entry.u && (
               <>
                  <Divider />

                  <Box
                     sx={{
                        px: { xs: 2, sm: 3 },
                        py: 2,
                        display: 'flex',
                        justifyContent: 'flex-end',
                     }}
                  >
                     <Button
                        variant="outlined"
                        endIcon={<OpenInNewIcon />}
                        onClick={() =>
                           window.open(
                              entry.u,
                              '_blank',
                              'noopener,noreferrer'
                           )
                        }
                        sx={{
                           color: '#163A5F',
                           borderColor: '#163A5F',
                           '&:hover': {
                              borderColor: '#0D2740',
                              bgcolor: '#F3F6F9',
                           },
                        }}
                     >
                        Open Bosch reference
                     </Button>
                  </Box>
               </>
            )}
         </Paper>
      )
   }

   return (
      <Box
         sx={{
            width: '100%',
            minHeight: 'calc(100vh - 130px)',
            bgcolor: '#F3F6F9',
            p: { xs: 2, md: 3 },
         }}
      >
         <Box
            sx={{
               maxWidth: 1050,
               mx: 'auto',
            }}
         >
            <Box
               sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 0.8,
               }}
            >
               <BuildCircleOutlinedIcon
                  sx={{
                     color: '#00A9CE',
                     fontSize: 30,
                  }}
               />

               <Typography
                  sx={{
                     color: '#172B4D',
                     fontWeight: 800,
                     fontSize: {
                        xs: '24px',
                        md: '28px',
                     },
                  }}
               >
                  Toolbox
               </Typography>
            </Box>

            <Typography
               sx={{
                  color: '#667085',
                  fontSize: '14px',
                  mb: 3,
               }}
            >
               Technical reference tools for calibration work.
            </Typography>

            {/* SWEDISH VEHICLE LOOKUP */}

            <Paper
               elevation={0}
               sx={{
                  mb: 3,
                  border: '1px solid #E4E7EC',
                  borderRadius: 2,
                  overflow: 'hidden',
               }}
            >
               <Box
                  sx={{
                     px: { xs: 2, sm: 3 },
                     py: 2.5,
                     bgcolor: '#FFFFFF',
                  }}
               >
                  <Box
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap',
                        mb: 2.5,
                     }}
                  >
                     <Box
                        sx={{
                           display: 'flex',
                           alignItems: 'center',
                           gap: 1.2,
                        }}
                     >
                        <Box
                           sx={{
                              width: 38,
                              height: 38,
                              display: 'grid',
                              placeItems: 'center',
                              borderRadius: 1.5,
                              bgcolor: '#E9F6FA',
                           }}
                        >
                           <DirectionsCarOutlinedIcon
                              sx={{
                                 color: '#163A5F',
                              }}
                           />
                        </Box>

                        <Box>
                           <Typography
                              sx={{
                                 color: '#172B4D',
                                 fontWeight: 800,
                                 fontSize: '17px',
                              }}
                           >
                              Swedish Vehicle Lookup
                           </Typography>

                           <Typography
                              sx={{
                                 color: '#667085',
                                 fontSize: '13px',
                              }}
                           >
                              Search Swedish vehicle data by
                              registration number
                           </Typography>
                        </Box>
                     </Box>

                     <Chip
                        label="Transportstyrelsen"
                        size="small"
                        sx={{
                           bgcolor: '#F2F4F7',
                           color: '#475467',
                           fontWeight: 700,
                        }}
                     />
                  </Box>

                  <Box
                     sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                           xs: '1fr',
                           sm: 'minmax(0, 1fr) auto',
                        },
                        gap: 1.5,
                     }}
                  >
                     <TextField
                        fullWidth
                        value={vehicleQuery}
                        onChange={(event) =>
                           setVehicleQuery(
                              event.target.value.toUpperCase()
                           )
                        }
                        onKeyDown={handleVehicleKeyDown}
                        label="Registration number"
                        placeholder="e.g. HUR194"
                        autoComplete="off"
                        inputProps={{
                           maxLength: 10,
                        }}
                        InputProps={{
                           startAdornment: (
                              <InputAdornment position="start">
                                 <SearchIcon
                                    sx={{
                                       color: '#98A2B3',
                                    }}
                                 />
                              </InputAdornment>
                           ),
                        }}
                        sx={{
                           '& .MuiOutlinedInput-root': {
                              bgcolor: '#FFFFFF',
                              '&.Mui-focused fieldset': {
                                 borderColor: '#163A5F',
                              },
                           },
                           '& .MuiInputLabel-root.Mui-focused':
                              {
                                 color: '#163A5F',
                              },
                        }}
                     />

                     <Button
                        onClick={searchVehicle}
                        variant="contained"
                        disabled={
                           !vehicleQuery.trim() ||
                           vehicleLoading
                        }
                        sx={{
                           minWidth: {
                              sm: 130,
                           },
                           px: 3,
                           bgcolor: '#163A5F',
                           '&:hover': {
                              bgcolor: '#0D2740',
                           },
                        }}
                     >
                        {vehicleLoading ? (
                           <CircularProgress
                              size={22}
                              color="inherit"
                           />
                        ) : (
                           'Search'
                        )}
                     </Button>
                  </Box>

                  <Typography
                     sx={{
                        color: '#98A2B3',
                        fontSize: '12px',
                        mt: 1.2,
                     }}
                  >
                     Spaces and hyphens are ignored. Vehicle and
                     technical data only.
                  </Typography>

                  {vehicleError && (
                     <Alert
                        severity="error"
                        sx={{
                           mt: 2.5,
                        }}
                     >
                        {vehicleError}
                     </Alert>
                  )}

                  {renderVehicleResult()}
               </Box>
            </Paper>

            {/* DTC LOOKUP */}

            <Paper
               elevation={0}
               sx={{
                  mb: 3,
                  border: '1px solid #E4E7EC',
                  borderRadius: 2,
                  overflow: 'hidden',
               }}
            >
               <Box
                  sx={{
                     px: { xs: 2, sm: 3 },
                     py: 2.5,
                     bgcolor: '#FFFFFF',
                  }}
               >
                  <Box
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap',
                        mb: 2.5,
                     }}
                  >
                     <Box
                        sx={{
                           display: 'flex',
                           alignItems: 'center',
                           gap: 1.2,
                        }}
                     >
                        <Box
                           sx={{
                              width: 38,
                              height: 38,
                              display: 'grid',
                              placeItems: 'center',
                              borderRadius: 1.5,
                              bgcolor: '#E9F6FA',
                           }}
                        >
                           <SearchIcon
                              sx={{
                                 color: '#163A5F',
                              }}
                           />
                        </Box>

                        <Box>
                           <Typography
                              sx={{
                                 color: '#172B4D',
                                 fontWeight: 800,
                                 fontSize: '17px',
                              }}
                           >
                              DTC Lookup
                           </Typography>

                           <Typography
                              sx={{
                                 color: '#667085',
                                 fontSize: '13px',
                              }}
                           >
                              Search diagnostic trouble codes
                           </Typography>
                        </Box>
                     </Box>

                     {!dtcLoading && !dtcLoadError && (
                        <Chip
                           label={`${dtcDatabaseCount.toLocaleString()} codes`}
                           size="small"
                           sx={{
                              bgcolor: '#F2F4F7',
                              color: '#475467',
                              fontWeight: 700,
                           }}
                        />
                     )}
                  </Box>

                  {dtcLoading ? (
                     <Box
                        sx={{
                           py: 5,
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           gap: 2,
                        }}
                     >
                        <CircularProgress size={24} />

                        <Typography
                           sx={{
                              color: '#667085',
                           }}
                        >
                           Loading DTC references...
                        </Typography>
                     </Box>
                  ) : dtcLoadError ? (
                     <Alert severity="error">
                        {dtcLoadError}
                     </Alert>
                  ) : (
                     <>
                        <Box
                           sx={{
                              display: 'grid',
                              gridTemplateColumns: {
                                 xs: '1fr',
                                 sm: 'minmax(0, 1fr) auto',
                              },
                              gap: 1.5,
                              alignItems: 'start',
                           }}
                        >
                           <TextField
                              fullWidth
                              multiline
                              minRows={1}
                              maxRows={5}
                              value={dtcQuery}
                              onChange={(event) =>
                                 setDtcQuery(
                                    event.target.value.toUpperCase()
                                 )
                              }
                              onKeyDown={handleDtcKeyDown}
                              label="DTC code(s)"
                              placeholder={
                                 'e.g. P0601\nP2002\nU0100'
                              }
                              autoComplete="off"
                              InputProps={{
                                 startAdornment: (
                                    <InputAdornment
                                       position="start"
                                       sx={{
                                          alignSelf:
                                             'flex-start',
                                          mt: 1.5,
                                       }}
                                    >
                                       <SearchIcon
                                          sx={{
                                             color:
                                                '#98A2B3',
                                          }}
                                       />
                                    </InputAdornment>
                                 ),
                              }}
                              sx={{
                                 '& .MuiOutlinedInput-root':
                                    {
                                       bgcolor: '#FFFFFF',
                                       '&.Mui-focused fieldset':
                                          {
                                             borderColor:
                                                '#163A5F',
                                          },
                                    },
                                 '& .MuiInputLabel-root.Mui-focused':
                                    {
                                       color: '#163A5F',
                                    },
                              }}
                           />

                           <Button
                              onClick={searchDtc}
                              variant="contained"
                              disabled={!dtcQuery.trim()}
                              sx={{
                                 minWidth: {
                                    sm: 130,
                                 },
                                 px: 3,
                                 minHeight: 56,
                                 bgcolor: '#163A5F',
                                 '&:hover': {
                                    bgcolor: '#0D2740',
                                 },
                              }}
                           >
                              Search
                           </Button>
                        </Box>

                        <Typography
                           sx={{
                              color: '#98A2B3',
                              fontSize: '12px',
                              mt: 1.2,
                           }}
                        >
                           Enter one or more P, B, C or U codes.
                           Separate multiple codes with spaces,
                           commas or new lines. Ctrl+Enter searches.
                        </Typography>

                        {dtcInputError && (
                           <Alert
                              severity="info"
                              sx={{
                                 mt: 2.5,
                              }}
                           >
                              {dtcInputError}
                           </Alert>
                        )}

                        {dtcResults.length > 0 && (
                           <Box
                              sx={{
                                 mt: 2.5,
                                 display: 'grid',
                                 gap: 1.5,
                              }}
                           >
                              {dtcResults.map(
                                 ({ code, entry }) =>
                                    entry ? (
                                       <Paper
                                          key={code}
                                          elevation={0}
                                          sx={{
                                             border:
                                                '1px solid #E4E7EC',
                                             borderRadius: 2,
                                             overflow:
                                                'hidden',
                                          }}
                                       >
                                          <Box
                                             sx={{
                                                px: {
                                                   xs: 2,
                                                   sm: 3,
                                                },
                                                py: 1.8,
                                                bgcolor:
                                                   '#F8FAFC',
                                                borderBottom:
                                                   '1px solid #E4E7EC',
                                                display:
                                                   'flex',
                                                alignItems:
                                                   'center',
                                                justifyContent:
                                                   'space-between',
                                                gap: 1.5,
                                                flexWrap:
                                                   'wrap',
                                             }}
                                          >
                                             <Typography
                                                sx={{
                                                   color:
                                                      '#172B4D',
                                                   fontWeight:
                                                      800,
                                                   fontSize:
                                                      '18px',
                                                   fontFamily:
                                                      'monospace',
                                                }}
                                             >
                                                {code}
                                             </Typography>

                                             <Box
                                                sx={{
                                                   display:
                                                      'flex',
                                                   gap: 1,
                                                   flexWrap:
                                                      'wrap',
                                                }}
                                             >
                                                <Chip
                                                   label={
                                                      entry.type ||
                                                      (code[0] ===
                                                      'P'
                                                         ? 'Powertrain'
                                                         : code[0] ===
                                                           'B'
                                                         ? 'Body'
                                                         : code[0] ===
                                                           'C'
                                                         ? 'Chassis'
                                                         : 'Network')
                                                   }
                                                   size="small"
                                                   sx={{
                                                      bgcolor:
                                                         '#E9F6FA',
                                                      color:
                                                         '#0D6F88',
                                                      fontWeight:
                                                         700,
                                                   }}
                                                />

                                                {Array.isArray(
                                                   entry.sources
                                                ) && (
                                                   <Chip
                                                      label={`${
                                                         entry
                                                            .sources
                                                            .length
                                                      } ${
                                                         entry
                                                            .sources
                                                            .length ===
                                                         1
                                                            ? 'reference'
                                                            : 'references'
                                                      }`}
                                                      size="small"
                                                      sx={{
                                                         bgcolor:
                                                            entry
                                                               .sources
                                                               .length >
                                                            1
                                                               ? '#ECFDF3'
                                                               : '#F2F4F7',
                                                         color:
                                                            entry
                                                               .sources
                                                               .length >
                                                            1
                                                               ? '#067647'
                                                               : '#475467',
                                                         fontWeight:
                                                            700,
                                                      }}
                                                   />
                                                )}
                                             </Box>
                                          </Box>

                                          <Box
                                             sx={{
                                                px: {
                                                   xs: 2,
                                                   sm: 3,
                                                },
                                                py: 2.2,
                                             }}
                                          >
                                             <Typography
                                                sx={{
                                                   color:
                                                      '#172B4D',
                                                   fontSize:
                                                      '15px',
                                                   fontWeight:
                                                      600,
                                                   lineHeight:
                                                      1.55,
                                                }}
                                             >
                                                {entry.description ||
                                                   'No description available.'}
                                             </Typography>
                                          </Box>
                                       </Paper>
                                    ) : (
                                       <Alert
                                          severity="info"
                                          key={code}
                                       >
                                          No DTC reference found
                                          for {code}.
                                       </Alert>
                                    )
                              )}
                           </Box>
                        )}
                     </>
                  )}
               </Box>
            </Paper>

            {/* BOSCH ECU LOOKUP */}

            <Paper
               elevation={0}
               sx={{
                  border: '1px solid #E4E7EC',
                  borderRadius: 2,
                  overflow: 'hidden',
               }}
            >
               <Box
                  sx={{
                     px: { xs: 2, sm: 3 },
                     py: 2.5,
                     bgcolor: '#FFFFFF',
                  }}
               >
                  <Box
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap',
                        mb: 2.5,
                     }}
                  >
                     <Box
                        sx={{
                           display: 'flex',
                           alignItems: 'center',
                           gap: 1.2,
                        }}
                     >
                        <Box
                           sx={{
                              width: 38,
                              height: 38,
                              display: 'grid',
                              placeItems: 'center',
                              borderRadius: 1.5,
                              bgcolor: '#E9F6FA',
                           }}
                        >
                           <MemoryIcon
                              sx={{
                                 color: '#163A5F',
                              }}
                           />
                        </Box>

                        <Box>
                           <Typography
                              sx={{
                                 color: '#172B4D',
                                 fontWeight: 800,
                                 fontSize: '17px',
                              }}
                           >
                              Bosch ECU Lookup
                           </Typography>

                           <Typography
                              sx={{
                                 color: '#667085',
                                 fontSize: '13px',
                              }}
                           >
                              Search by Bosch manufacturer number
                           </Typography>
                        </Box>
                     </Box>

                     {!loading && !loadError && (
                        <Chip
                           label={`${databaseCount.toLocaleString()} references`}
                           size="small"
                           sx={{
                              bgcolor: '#F2F4F7',
                              color: '#475467',
                              fontWeight: 700,
                           }}
                        />
                     )}
                  </Box>

                  {loading ? (
                     <Box
                        sx={{
                           py: 5,
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           gap: 2,
                        }}
                     >
                        <CircularProgress size={24} />

                        <Typography
                           sx={{
                              color: '#667085',
                           }}
                        >
                           Loading Bosch references...
                        </Typography>
                     </Box>
                  ) : loadError ? (
                     <Alert severity="error">
                        {loadError}
                     </Alert>
                  ) : (
                     <>
                        <Box
                           sx={{
                              display: 'grid',
                              gridTemplateColumns: {
                                 xs: '1fr',
                                 sm: 'minmax(0, 1fr) auto',
                              },
                              gap: 1.5,
                           }}
                        >
                           <TextField
                              fullWidth
                              value={query}
                              onChange={(event) =>
                                 setQuery(event.target.value)
                              }
                              onKeyDown={handleKeyDown}
                              label="Bosch number"
                              placeholder="e.g. 0281014329"
                              autoComplete="off"
                              InputProps={{
                                 startAdornment: (
                                    <InputAdornment position="start">
                                       <SearchIcon
                                          sx={{
                                             color:
                                                '#98A2B3',
                                          }}
                                       />
                                    </InputAdornment>
                                 ),
                              }}
                              sx={{
                                 '& .MuiOutlinedInput-root':
                                    {
                                       bgcolor: '#FFFFFF',
                                       '&.Mui-focused fieldset':
                                          {
                                             borderColor:
                                                '#163A5F',
                                          },
                                    },
                                 '& .MuiInputLabel-root.Mui-focused':
                                    {
                                       color: '#163A5F',
                                    },
                              }}
                           />

                           <Button
                              onClick={search}
                              variant="contained"
                              disabled={!query.trim()}
                              sx={{
                                 minWidth: {
                                    sm: 130,
                                 },
                                 px: 3,
                                 bgcolor: '#163A5F',
                                 '&:hover': {
                                    bgcolor: '#0D2740',
                                 },
                              }}
                           >
                              Search
                           </Button>
                        </Box>

                        <Typography
                           sx={{
                              color: '#98A2B3',
                              fontSize: '12px',
                              mt: 1.2,
                           }}
                        >
                           Spaces and punctuation are ignored.
                           Example: 0 281 014 329 → 0281014329.
                        </Typography>

                        {searchedNumber && !result && (
                           <Alert
                              severity="info"
                              sx={{
                                 mt: 2.5,
                              }}
                           >
                              No Bosch reference found for{' '}
                              {searchedNumber}.
                           </Alert>
                        )}

                        {Array.isArray(result)
                           ? result.map((entry, index) =>
                                renderResult(entry, index)
                             )
                           : result
                           ? renderResult(result)
                           : null}
                     </>
                  )}
               </Box>
            </Paper>
         </Box>
      </Box>
   )
}